import type {
  SnackBarConfig,
} from '@shared/components/Snackbar';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { Box, Card, Chip, Container as MuiContainer, Paper, Tab, Tabs, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ManageFollowedCompaniesButton } from '@shared/components/Buttons/FollowCompaniesButton';
import CompanyLogo from '@shared/components/CompanyLogo';
import CustomSnackbar from '@shared/components/Snackbar';
import StaticTextCard from '@shared/components/StaticTextCard';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { UserContext, UserLoadState } from '@/shared/contexts/UserContext';
import { UserType } from '@shared/domain/user';
import { animations, commonCards, commonContainers, commonTypography, gradients } from '@shared/theme/patterns';
import ChangePassword from './components/ChangePassword';
import OfferingsPage from './components/OfferingsPage';
import ProfilePage from './components/ProfilePage';

function SettingsPage() {
  const { user, userLoadState } = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const [value, setValue] = useState(searchParams.get('tab') ?? '0');
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();

  const navigate = useNavigate();

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const handleSnackBarClose = () => {
    setSnackbarConfig({ ...snackbarConfig, open: false });
  };

  if (userLoadState === UserLoadState.None) {
    return <LoadingSpinner variant="circular" size="large" message="Loading account settings..." fullScreen />;
  }

  if (!user) {
    return (
      <StaticTextCard
        header="Not a Member!"
        text="It seems that you are not yet a member of Kandir, please head to our sign-up page."
        link={true}
      />
    );
  }

  return (
    <Box
      className="account-settings-page-outer-container"
    >
      <MuiContainer className="account-settings-page-container" maxWidth="lg" sx={commonContainers.page}>
        {/* Hero Header Section */}
        <Paper
          className="account-settings-page-header"
          elevation={0}
          sx={{
            background: 'transparent',
            borderRadius: 3,
            mb: 1.5,
            textAlign: 'center',
            ...animations.slideUp,
          }}
        >
          <h1 className="page-title mb-2">
            Account Settings
          </h1>
          <p className="page-subtitle">
            Manage your account preferences, offerings, and followed companies all in one place.
          </p>
        </Paper>

        {/* Settings Content Card */}
        <Card sx={{ ...commonCards.elevated, p: 0, overflow: 'hidden' }}>
          <TabContext value={value}>
            {/* Tab Bar with Enhanced Styling */}
            <Box
              sx={{
                background: gradients.primary,
                borderRadius: '12px 12px 0 0',
                color: 'white',
                px: 2,
                py: 1,
              }}
            >
              <Tabs
                onChange={handleTabChange}
                centered
                value={value}
                sx={{
                  '& .MuiTab-root': {
                    color: 'rgba(255,255,255,0.7)',
                    fontWeight: 600,
                    minWidth: 0,
                    px: 3,
                    textTransform: 'none',
                    transition: 'all 0.3s ease',
                  },
                  '& .MuiTab-root:hover': {
                    color: 'white',
                    transform: 'translateY(-2px)',
                  },
                  '& .MuiTab-root.Mui-selected': {
                    color: 'white',
                    fontWeight: 700,
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'white',
                    borderRadius: '2px 2px 0 0',
                    height: 3,
                  },
                }}
              >
                <Tab
                  label="Profile"
                  value="0"
                  data-testid="settings-tab-profile"
                />
                {user?.userType !== UserType.COMPANY && user?.planSlug !== 'contributor' && (
                  <Tab
                    label="Offerings"
                    value="1"
                    data-testid="settings-tab-offerings"
                  />
                )}
                <Tab
                  label={
                    user?.userType === UserType.COMPANY
                      ? 'Users Following'
                      : 'Followed Companies'
                  }
                  value="3"
                  data-testid="settings-tab-following"
                />
                <Tab
                  label="Security"
                  value="4"
                  data-testid="settings-tab-change-password"
                />
              </Tabs>
            </Box>
            {/* Tab Panels with Enhanced Content */}
            <Box sx={{ p: 2 }}>
              <TabPanel value="0" sx={{ p: 0 }}>
                <ProfilePage embedded={true} />
              </TabPanel>

              {user?.userType !== UserType.COMPANY && user?.planSlug !== 'contributor' && user?.accountMemberships?.[0]?.accountId && (
                <TabPanel value="1" sx={{ p: 0 }}>
                  <OfferingsPage embedded={true} />
                </TabPanel>
              )}

              <TabPanel value="3" sx={{ p: 0 }}>
                <Box sx={{ ...animations.fadeIn }}>
                  {/* Companies Section */}
                  <Paper
                    sx={{
                      ...commonCards.bordered,
                      background: gradients.subtle,
                      mb: 3,
                    }}
                  >
                    {user?.followedCompanies && user?.followedCompanies.length === 0
                      ? (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                              No companies followed yet
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                              Start following companies to see their profiles here and get relevant content updates.
                            </Typography>
                          </Box>
                        )
                      : (
                          <Box>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                              Follow companies you're selling to or interested in. Kandir will show relevant content and notify you as new content is added.
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2,
                              }}
                            >
                              {user?.followedCompanies && user?.followedCompanies.map(company => (
                                <Chip
                                  key={company.id}
                                  sx={{
                                    '&:hover': {
                                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                                      transform: 'translateY(-2px)',
                                    },
                                    'backgroundColor': 'background.paper',
                                    'borderColor': 'primary.light',
                                    'cursor': 'pointer',
                                    'height': '48px',
                                    'transition': 'all 0.3s ease',
                                    'px': 1,
                                  }}
                                  avatar={(
                                    <CompanyLogo
                                      companyId={company.id}
                                      companyName={company?.companyName ?? ''}
                                      companyUrl={company?.url ?? ''}
                                      sx={{ width: 32, height: 32 }}
                                      alt={company?.companyName}
                                    />
                                  )}
                                  label={company.companyName}
                                  variant="outlined"
                                  onClick={() => {
                                    navigate(`/company/${encodeURIComponent(company.companyName!)}`);
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                      <ManageFollowedCompaniesButton highlighted={!user?.followedCompanies || user?.followedCompanies.length === 0} />
                    </Box>
                  </Paper>
                </Box>
              </TabPanel>

              <TabPanel value="4" sx={{ p: 0 }}>
                <Box sx={{ ...animations.fadeIn }}>
                  <Paper sx={{ ...commonCards.bordered, p: 3 }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          ...commonTypography.headingBordered,
                          '&::after': {
                            bgcolor: 'primary.main',
                            content: '""',
                            display: 'block',
                            height: 3,
                            mt: 1,
                            width: 50,
                          },
                        }}
                      >
                        Change Password
                      </Typography>
                    </Box>
                    <ChangePassword />
                  </Paper>
                </Box>
              </TabPanel>
            </Box>
          </TabContext>
        </Card>

        <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
      </MuiContainer>
    </Box>
  );
}

export default SettingsPage;
